import React, { useState, useCallback, useMemo, useEffect, useRef, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  BarChart3, 
  Package, 
  Users, 
  FileText, 
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  CreditCard,
  DollarSign,
  Bell,
  Settings,
  Search,
  Calendar,
  ChevronDown,
  ChevronRight,
  Menu,
  Activity,
  PlusCircle,
  User,
  LogOut,
  RefreshCw,
  Filter,
  PanelLeftClose,
  PanelLeftOpen,
  Moon,
  Sun,
  ArrowUpRight, 
  ArrowDownRight,
  Clock,
  X,
  Plus,
  Calculator,
  Receipt,
  ClipboardList,
  Eye,
  Edit,
  Trash2,
  Download,
  Send,
  CheckCircle,
  AlertCircle,
  Info,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';

// Credit Note History Modal Component
const CreditNoteHistoryModal = ({ 
  creditNotes, 
  loading, 
  pagination, 
  filters, 
  onFilterChange, 
  onRefresh, 
  onClose, 
  onApplyCredit, 
  onPageChange 
}) => {

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
            <ClipboardList size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Credit Note History</h3>
            <p className="text-sm text-gray-500">Manage and view all credit notes</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={onRefresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>


      {/* Search and Filters */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Search credit notes..." 
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            />
          </div>
          <select 
            value={filters.status}
            onChange={(e) => onFilterChange('status', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="applied">Applied</option>
            <option value="expired">Expired</option>
          </select>
          <select 
            value={filters.type}
            onChange={(e) => onFilterChange('type', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Types</option>
            <option value="discount">Discount</option>
            <option value="refund">Refund</option>
            <option value="adjustment">Adjustment</option>
            <option value="return">Return</option>
          </select>
          <div className="flex gap-2">
            <input 
              type="date" 
              value={filters.start_date}
              onChange={(e) => onFilterChange('start_date', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Start Date"
            />
            <input 
              type="date" 
              value={filters.end_date}
              onChange={(e) => onFilterChange('end_date', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="End Date"
            />
          </div>
        </div>
      </div>

      {/* Credit Notes Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Credit Note
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Applied
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Remaining
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 className="animate-spin h-10 w-10 text-blue-600 mb-4" />
                      <p className="text-gray-600 font-medium">Loading credit notes...</p>
                      <p className="text-gray-500 text-sm">Please wait while we fetch your data</p>
                    </div>
                  </td>
                </tr>
              ) : (creditNotes || []).length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <FileText className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No credit notes found</h3>
                      <p className="text-gray-500 text-sm mb-4">Try adjusting your search criteria or filters</p>
                      <button 
                        onClick={onRefresh}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                      >
                        Refresh Data
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                (creditNotes || []).map((creditNote) => (
                  <tr key={creditNote.id} className="hover:bg-gray-50 transition-colors duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <FileText size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {creditNote.credit_note_id}
                          </div>
                          <div className="text-xs text-gray-500 max-w-xs truncate">
                            {creditNote.reason || 'No reason provided'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                          <User size={14} className="text-green-600" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {creditNote.customer_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {creditNote.customer_phone || 'No phone'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">
                        ₵{parseFloat(creditNote.credit_amount || 0).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-orange-600">
                        ₵{parseFloat(creditNote.applied_amount || 0).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-green-600">
                        ₵{parseFloat(creditNote.remaining_amount || 0).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        creditNote.type === 'discount' ? 'bg-blue-100 text-blue-800' :
                        creditNote.type === 'refund' ? 'bg-green-100 text-green-800' :
                        creditNote.type === 'adjustment' ? 'bg-yellow-100 text-yellow-800' :
                        creditNote.type === 'return' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {creditNote.type?.charAt(0).toUpperCase() + creditNote.type?.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        creditNote.status === 'active' ? 'bg-green-100 text-green-800' :
                        creditNote.status === 'applied' ? 'bg-blue-100 text-blue-800' :
                        creditNote.status === 'expired' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {creditNote.status?.charAt(0).toUpperCase() + creditNote.status?.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(creditNote.issue_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {parseFloat(creditNote.remaining_amount || 0) > 0 && (
                          <button 
                            onClick={() => onApplyCredit(creditNote)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-200"
                            title="Apply Credit"
                          >
                            <CreditCard size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            // TODO: Implement view details functionality
                            toast.success(`Viewing details for ${creditNote.credit_note_id}`);
                          }}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-all duration-200"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => {
                            // TODO: Implement download functionality
                            toast.success(`Downloading ${creditNote.credit_note_id}`);
                          }}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-all duration-200"
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              <span className="font-medium">Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}</span>
              <span className="mx-1">to</span>
              <span className="font-medium">{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}</span>
              <span className="mx-1">of</span>
              <span className="font-medium">{pagination.totalItems}</span>
              <span className="ml-1">results</span>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.currentPage - 2)) + i;
                  if (pageNum > pagination.totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        pageNum === pagination.currentPage
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button 
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Credit Reports Modal Component
const CreditReportsModal = ({ 
  reportsData, 
  loading, 
  reportFilters, 
  onFilterChange, 
  onRefresh, 
  onClose, 
  onExportPDF, 
  onExportExcel 
}) => {
  const { summary, analytics, trends, topCustomers, recentActivity } = reportsData;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
            <BarChart3 size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Credit Reports Dashboard</h3>
            <p className="text-sm text-gray-500">Comprehensive credit note analytics and insights</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={onRefresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input 
              type="date" 
              value={reportFilters.start_date}
              onChange={(e) => onFilterChange('start_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input 
              type="date" 
              value={reportFilters.end_date}
              onChange={(e) => onFilterChange('end_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select 
              value={reportFilters.type}
              onChange={(e) => onFilterChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Types</option>
              <option value="discount">Discount</option>
              <option value="refund">Refund</option>
              <option value="adjustment">Adjustment</option>
              <option value="return">Return</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select 
              value={reportFilters.status}
              onChange={(e) => onFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="applied">Applied</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Credit Notes</p>
              <p className="text-2xl font-bold text-blue-900">
                {loading ? <Loader2 className="animate-spin" size={24} /> : summary.total_credit_notes || 0}
              </p>
            </div>
            <FileText className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Total Value</p>
              <p className="text-2xl font-bold text-green-900">
                {loading ? <Loader2 className="animate-spin" size={24} /> : `₵${parseFloat(summary.total_amount || 0).toFixed(2)}`}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Applied Credits</p>
              <p className="text-2xl font-bold text-orange-900">
                {loading ? <Loader2 className="animate-spin" size={24} /> : `₵${parseFloat(summary.applied_amount || 0).toFixed(2)}`}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-orange-500" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Available Credits</p>
              <p className="text-2xl font-bold text-purple-900">
                {loading ? <Loader2 className="animate-spin" size={24} /> : `₵${parseFloat(summary.remaining_amount || 0).toFixed(2)}`}
              </p>
            </div>
            <CreditCard className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trends Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Trends</h3>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin h-8 w-8 text-orange-600" />
            </div>
          ) : trends.length > 0 ? (
            <div className="space-y-4">
              {trends.map((trend, index) => (
                <div key={trend.month} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">
                      {new Date(trend.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">{trend.count} notes</div>
                    <div className="text-xs text-gray-500">₵{trend.amount.toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p>No trend data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Top Customers */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Customers by Credit Value</h3>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin h-8 w-8 text-orange-600" />
            </div>
          ) : topCustomers.length > 0 ? (
            <div className="space-y-3">
              {topCustomers.slice(0, 5).map((customer, index) => (
                <div key={customer.customer_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-orange-600">#{index + 1}</span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{customer.customer_name}</div>
                      <div className="text-xs text-gray-500">{customer.count} credit notes</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">₵{customer.total_amount.toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p>No customer data available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Credit Activity</h3>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="animate-spin h-8 w-8 text-orange-600" />
          </div>
        ) : recentActivity.length > 0 ? (
          <div className="space-y-3">
            {recentActivity.map((activity, index) => (
              <div key={activity.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <FileText size={14} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {activity.credit_note_id} - {activity.customer_name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {activity.type?.charAt(0).toUpperCase() + activity.type?.slice(1)} • 
                      {new Date(activity.issue_date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-900">₵{parseFloat(activity.credit_amount || 0).toFixed(2)}</div>
                  <div className="text-xs text-gray-500">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      activity.status === 'active' ? 'bg-green-100 text-green-800' :
                      activity.status === 'applied' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {activity.status?.charAt(0).toUpperCase() + activity.status?.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <Activity className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p>No recent activity</p>
            </div>
          </div>
        )}
      </div>

      {/* Export Actions */}
      <div className="flex justify-end space-x-3">
        <button 
          onClick={onExportPDF}
          className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center gap-2"
        >
          <Download size={16} />
          Export PDF
        </button>
        <button 
          onClick={onExportExcel}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center gap-2"
        >
          <Download size={16} />
          Export Excel
        </button>
        <button 
          onClick={onClose}
          className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
        >
          Close
        </button>
      </div>
    </div>
  );
};

const CreditNote = () => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  
  // Professional state management
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersWithCredits, setCustomersWithCredits] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [creditNoteStats, setCreditNoteStats] = useState({});
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  
  // Form states
  const [createForm, setCreateForm] = useState({
    customer_id: '',
    credit_amount: '',
    reason: '',
    type: 'discount',
    related_document_type: '',
    related_document_id: ''
  });
  
  const [applyForm, setApplyForm] = useState({
    customer_id: '',
    credit_note_id: '',
    amount: '',
    related_document_type: '',
    related_document_id: ''
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    customer_id: '',
    search: '',
    start_date: '',
    end_date: ''
  });

  // Reports states
  const [reportsData, setReportsData] = useState({
    summary: {},
    analytics: {},
    trends: [],
    topCustomers: [],
    recentActivity: []
  });
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    start_date: '',
    end_date: '',
    type: 'all',
    status: 'all'
  });


  const handleCardClick = useCallback((href) => {
    console.log(`Navigating to: ${href}`);
    navigate(href);
  }, [navigate]);

  const handleModalOpen = useCallback((modalType) => {
    setActiveModal(modalType);
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setActiveModal(null);
    // Reset forms when closing modals
    setCreateForm({
      customer_id: '',
      credit_amount: '',
      reason: '',
      type: 'discount',
      related_document_type: '',
      related_document_id: ''
    });
    setApplyForm({
      customer_id: '',
      credit_note_id: '',
      amount: '',
      related_document_type: '',
      related_document_id: ''
    });
    // Reset customer search state
    setCustomerSearchTerm('');
    setSelectedCustomerName('');
    setShowCustomerDropdown(false);
  }, []);

  // Professional data fetching functions
  const fetchCreditNoteStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getCreditNoteStats();
      if (response.success) {
        setCreditNoteStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching credit note stats:', error);
      toast.error('Failed to fetch credit note statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      // Fetch all customers by setting a high per_page limit
      const response = await apiService.getCustomers('per_page=1000');
      if (response.success) {
        setCustomers(response.customers || []);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to fetch customers');
      setCustomers([]);
    }
  }, []);

  const fetchCustomersWithCredits = useCallback(async () => {
    try {
      const response = await apiService.getCustomersWithCredits();
      if (response.success) {
        setCustomersWithCredits(response.data);
      }
    } catch (error) {
      console.error('Error fetching customers with credits:', error);
      toast.error('Failed to fetch customers with credits');
    }
  }, []);

  const fetchCreditNotes = useCallback(async (page = 1, customFilters = null) => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = { page };
      
      // Use custom filters if provided, otherwise use current filters
      const currentFilters = customFilters || filters;
      
      // Add filters if they exist
      if (currentFilters.search) params.search = currentFilters.search;
      if (currentFilters.status && currentFilters.status !== 'all') params.status = currentFilters.status;
      if (currentFilters.type && currentFilters.type !== 'all') params.type = currentFilters.type;
      if (currentFilters.start_date) params.start_date = currentFilters.start_date;
      if (currentFilters.end_date) params.end_date = currentFilters.end_date;
      
      const response = await apiService.getCreditNotes(params);
      
      if (response.success) {
        setCreditNotes(response.data || []);
        setPagination({
          currentPage: response.pagination?.current_page || 1,
          totalPages: response.pagination?.last_page || 1,
          totalItems: response.pagination?.total || 0,
          itemsPerPage: response.pagination?.per_page || 15
        });
      } else {
        toast.error(response.message || 'Failed to fetch credit notes');
      }
    } catch (error) {
      console.error('Error fetching credit notes:', error);
      toast.error('Failed to fetch credit notes');
    } finally {
      setLoading(false);
    }
  }, []);

  // Form handlers
  const handleCreateFormChange = useCallback((field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleApplyFormChange = useCallback((field, value) => {
    setApplyForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleReportFilterChange = useCallback((field, value) => {
    setReportFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm) return customers;
    return customers.filter(customer => 
      customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
      customer.phone?.includes(customerSearchTerm)
    );
  }, [customers, customerSearchTerm]);

  // Handle customer selection
  const handleCustomerSelect = useCallback((customer) => {
    setCreateForm(prev => ({ ...prev, customer_id: customer.id }));
    setSelectedCustomerName(customer.name);
    setCustomerSearchTerm(''); // Clear search term on selection
    setShowCustomerDropdown(false);
  }, []);

  // Fetch reports data
  const fetchReportsData = useCallback(async (customFilters = null) => {
    try {
      setReportsLoading(true);
      const filters = customFilters || reportFilters;
      
      // Fetch summary data
      const summaryResponse = await apiService.getCreditNoteStats(filters);
      
      // Fetch analytics data (if available)
      const analyticsResponse = await apiService.getCreditNotes({
        ...filters,
        per_page: 1000 // Get more data for analytics
      });

      if (summaryResponse.success && analyticsResponse.success) {
        // Process the data for reports
        const processedData = {
          summary: summaryResponse.data || {},
          analytics: analyticsResponse.data || [],
          trends: [], // Will be calculated from analytics data
          topCustomers: [], // Will be calculated from analytics data
          recentActivity: analyticsResponse.data?.slice(0, 10) || []
        };

        // Calculate trends (monthly data)
        if (analyticsResponse.data) {
          const monthlyData = {};
          analyticsResponse.data.forEach(creditNote => {
            const month = new Date(creditNote.issue_date).toISOString().substring(0, 7);
            if (!monthlyData[month]) {
              monthlyData[month] = { count: 0, amount: 0 };
            }
            monthlyData[month].count += 1;
            monthlyData[month].amount += parseFloat(creditNote.credit_amount || 0);
          });
          processedData.trends = Object.entries(monthlyData).map(([month, data]) => ({
            month,
            count: data.count,
            amount: data.amount
          })).sort((a, b) => a.month.localeCompare(b.month));

          // Calculate top customers
          const customerData = {};
          analyticsResponse.data.forEach(creditNote => {
            const customerId = creditNote.customer_id;
            if (!customerData[customerId]) {
              customerData[customerId] = {
                customer_id: customerId,
                customer_name: creditNote.customer_name,
                count: 0,
                total_amount: 0
              };
            }
            customerData[customerId].count += 1;
            customerData[customerId].total_amount += parseFloat(creditNote.credit_amount || 0);
          });
          processedData.topCustomers = Object.values(customerData)
            .sort((a, b) => b.total_amount - a.total_amount)
            .slice(0, 10);
        }

        setReportsData(processedData);
      }
    } catch (error) {
      console.error('Error fetching reports data:', error);
      toast.error('Failed to fetch reports data');
    } finally {
      setReportsLoading(false);
    }
  }, [reportFilters]);

  // Form submissions
  const handleCreateCreditNote = useCallback(async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!createForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!createForm.credit_amount || parseFloat(createForm.credit_amount) <= 0) {
      toast.error('Please enter a valid credit amount');
      return;
    }
    if (!createForm.reason || createForm.reason.length < 10) {
      toast.error('Reason must be at least 10 characters long');
      return;
    }
    if (!createForm.type) {
      toast.error('Please select a credit note type');
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiService.createCreditNote(createForm);
      if (response.success) {
        toast.success('Credit note created successfully');
        handleModalClose();
        fetchCreditNotes(1, filters);
        fetchCreditNoteStats();
        fetchCustomersWithCredits();
      } else {
        // Show specific validation errors if available
        if (response.errors) {
          const errorMessages = Object.values(response.errors).flat();
          errorMessages.forEach(error => toast.error(error));
        } else {
          toast.error(response.message || 'Failed to create credit note');
        }
      }
    } catch (error) {
      console.error('Error creating credit note:', error);
      if (error.message && error.message.includes('422')) {
        toast.error('Validation failed. Please check your input.');
      } else {
      toast.error('Failed to create credit note');
      }
    } finally {
      setLoading(false);
    }
  }, [createForm, handleModalClose, fetchCreditNotes, fetchCreditNoteStats, fetchCustomersWithCredits]);

  const handleApplyCredit = useCallback(async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!applyForm.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!applyForm.credit_note_id) {
      toast.error('Please select a credit note');
      return;
    }
    if (!applyForm.amount || parseFloat(applyForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiService.applyCredit(applyForm.credit_note_id, {
        amount: applyForm.amount,
        related_document_type: applyForm.related_document_type,
        related_document_id: applyForm.related_document_id
      });
      if (response.success) {
        toast.success('Credit applied successfully');
        handleModalClose();
        fetchCreditNotes(1, filters);
        fetchCreditNoteStats();
        fetchCustomersWithCredits();
      } else {
        // Show specific validation errors if available
        if (response.errors) {
          const errorMessages = Object.values(response.errors).flat();
          errorMessages.forEach(error => toast.error(error));
        } else {
          toast.error(response.message || 'Failed to apply credit');
        }
      }
    } catch (error) {
      console.error('Error applying credit:', error);
      if (error.message && error.message.includes('422')) {
        toast.error('Validation failed. Please check your input.');
      } else {
      toast.error('Failed to apply credit');
      }
    } finally {
      setLoading(false);
    }
  }, [applyForm, handleModalClose, fetchCreditNotes, fetchCreditNoteStats, fetchCustomersWithCredits]);

  // Load data on component mount
  useEffect(() => {
    fetchCreditNoteStats();
    fetchCustomers();
    fetchCustomersWithCredits();
    fetchCreditNotes();
  }, []);

  // Refetch credit notes when filters change
  useEffect(() => {
    fetchCreditNotes(1, filters);
  }, [filters, fetchCreditNotes]);

  // Fetch credit notes when history modal opens
  useEffect(() => {
    if (activeModal === 'credit-note-history' && modalOpen) {
      fetchCreditNotes(1, filters);
    }
  }, [activeModal, modalOpen, fetchCreditNotes, filters]);

  // Fetch reports data when reports modal opens
  useEffect(() => {
    if (activeModal === 'credit-reports' && modalOpen) {
      fetchReportsData();
    }
  }, [activeModal, modalOpen, fetchReportsData]);

  // Refetch reports data when report filters change
  useEffect(() => {
    if (activeModal === 'credit-reports' && modalOpen) {
      fetchReportsData();
    }
  }, [reportFilters, activeModal, modalOpen, fetchReportsData]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && !event.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen, showCustomerDropdown]);

  return (
    <Layout>
      <main className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2"></h1>
            </div>
          </div>

          {/* Credit Note Content */}
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Credit Note Management</h1>
              <p className="text-xl text-gray-600">Manage your credit notes and customer refunds efficiently</p>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Total Credit Notes</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {loading ? <Loader2 className="animate-spin" size={24} /> : creditNoteStats.total_credit_notes || 0}
                    </p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-500" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Total Value</p>
                    <p className="text-2xl font-bold text-green-900">
                      {loading ? <Loader2 className="animate-spin" size={24} /> : `₵${parseFloat(creditNoteStats.total_amount || 0).toFixed(2)}`}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600">Applied Credits</p>
                    <p className="text-2xl font-bold text-orange-900">
                      {loading ? <Loader2 className="animate-spin" size={24} /> : `₵${parseFloat(creditNoteStats.applied_amount || 0).toFixed(2)}`}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-orange-500" />
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600">Remaining Credits</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {loading ? <Loader2 className="animate-spin" size={24} /> : `₵${parseFloat(creditNoteStats.remaining_amount || 0).toFixed(2)}`}
                    </p>
                  </div>
                  <CreditCard className="h-8 w-8 text-purple-500" />
                </div>
              </div>
            </div>

            {/* Four Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Create Credit Note */}
              <div className="bg-gradient-to-br from-white to-blue-50/40 rounded-2xl p-8 border border-blue-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                    <FileText size={32} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Create Credit Note</h3>
                    <p className="text-gray-600">Create a new credit note for customer refunds</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleModalOpen('create-credit-note')}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                >
                  Create New Credit Note
                </button>
              </div>

              {/* Credit Note History */}
              <div className="bg-gradient-to-br from-white to-green-50/40 rounded-2xl p-8 border border-green-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-400 via-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                    <ClipboardList size={32} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Credit Note History</h3>
                    <p className="text-gray-600">View and manage all credit notes</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleModalOpen('credit-note-history')}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                >
                  View History
                </button>
              </div>

              {/* Apply Credit */}
              <div className="bg-gradient-to-br from-white to-purple-50/40 rounded-2xl p-8 border border-purple-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-400 via-purple-500 to-violet-500 rounded-xl flex items-center justify-center shadow-lg">
                    <CreditCard size={32} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Apply Credit</h3>
                    <p className="text-gray-600">Apply credit to customer accounts</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleModalOpen('apply-credit')}
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                >
                  Apply Credit
                </button>
              </div>

              {/* Credit Reports */}
              <div className="bg-gradient-to-br from-white to-orange-50/40 rounded-2xl p-8 border border-orange-100 hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                    <BarChart3 size={32} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Credit Reports</h3>
                    <p className="text-gray-600">Generate detailed credit note reports</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleModalOpen('credit-reports')}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
                >
                  Generate Reports
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      {activeModal === 'create-credit-note' && <FileText size={24} className="text-white" />}
                      {activeModal === 'credit-history' && <ClipboardList size={24} className="text-white" />}
                      {activeModal === 'apply-credit' && <CreditCard size={24} className="text-white" />}
                      {activeModal === 'credit-reports' && <BarChart3 size={24} className="text-white" />}
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      {activeModal === 'create-credit-note' && 'Create Credit Note'}
                      {activeModal === 'credit-history' && 'Credit Note History'}
                      {activeModal === 'apply-credit' && 'Apply Credit'}
                      {activeModal === 'credit-reports' && 'Credit Reports'}
                    </h2>
                  </div>
                  <button
                    onClick={handleModalClose}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {activeModal === 'create-credit-note' && (
                  <form onSubmit={handleCreateCreditNote} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
                        <div className="relative customer-dropdown-container">
                          <div
                            onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                            className="w-full px-4 py-3 bg-white text-gray-700 border-2 border-blue-500 rounded-lg cursor-pointer flex items-center justify-between hover:bg-gray-50 transition-colors"
                          >
                            <span className={selectedCustomerName ? "text-gray-900" : "text-gray-400"}>
                              {selectedCustomerName || "Choose a customer..."}
                            </span>
                            <ChevronDown 
                              size={16} 
                              className={`text-gray-400 transition-transform ${showCustomerDropdown ? 'rotate-180' : ''}`} 
                            />
                          </div>
                          {showCustomerDropdown && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                              {/* Search Bar */}
                              <div className="p-3 border-b border-gray-100">
                                <div className="relative">
                                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                                  <input
                                    type="text"
                                    value={customerSearchTerm}
                                    onChange={(e) => {
                                      setCustomerSearchTerm(e.target.value);
                                      if (!e.target.value) {
                                        setCreateForm(prev => ({ ...prev, customer_id: '' }));
                                      }
                                    }}
                                    placeholder="Search customer..."
                                    className="w-full pl-10 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white"
                                  />
                                </div>
                              </div>
                              
                              {/* Customer Count */}
                              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                                <span className="text-sm font-medium text-gray-700">
                                  {filteredCustomers.length} of {customers.length} customers
                                </span>
                              </div>
                              
                              {/* Customer List */}
                              <div className="max-h-60 overflow-y-auto">
                                {filteredCustomers.length > 0 ? (
                                  filteredCustomers.map(customer => (
                                    <div
                                      key={customer.id}
                                      onClick={() => handleCustomerSelect(customer)}
                                      className="px-3 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                    >
                                      <div className="font-bold text-gray-900 uppercase text-sm">
                                        {customer.name}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="px-3 py-4 text-gray-500 text-center">
                                    No customers found
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {createForm.customer_id && (
                          <div className="mt-2 text-sm text-green-600">
                            ✓ Customer selected
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Credit Amount *</label>
                        <input 
                          type="number" 
                          step="0.01"
                          min="0"
                          value={createForm.credit_amount}
                          onChange={(e) => handleCreateFormChange('credit_amount', e.target.value)}
                          placeholder="0.00" 
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                        <select 
                          value={createForm.type}
                          onChange={(e) => handleCreateFormChange('type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="discount">Discount</option>
                          <option value="return">Return</option>
                          <option value="refund">Refund</option>
                          <option value="adjustment">Adjustment</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Related Document Type</label>
                        <select 
                          value={createForm.related_document_type}
                          onChange={(e) => handleCreateFormChange('related_document_type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
                        >
                          <option value="">Select Document Type</option>
                          <option value="Invoice">Invoice</option>
                          <option value="Sale">Sale</option>
                          <option value="Proforma">Proforma</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reason *</label>
                      <textarea 
                        rows={4} 
                        value={createForm.reason}
                        onChange={(e) => handleCreateFormChange('reason', e.target.value)}
                        placeholder="Enter reason for credit note (minimum 10 characters)..." 
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          createForm.reason && createForm.reason.length < 10 
                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                            : 'border-gray-300'
                        }`}
                        required
                        minLength={10}
                      />
                      {createForm.reason && createForm.reason.length < 10 && (
                        <p className="text-red-500 text-sm mt-1">
                          Reason must be at least 10 characters ({createForm.reason.length}/10)
                        </p>
                      )}
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button 
                        type="button"
                        onClick={handleModalClose} 
                        className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        disabled={loading}
                      >
                        {loading ? <Loader2 className="animate-spin" size={16} /> : null}
                        <span>Create Credit Note</span>
                      </button>
                    </div>
                  </form>
                )}

                {activeModal === 'credit-note-history' && (
                  <CreditNoteHistoryModal 
                    creditNotes={creditNotes}
                    loading={loading}
                    pagination={pagination}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onRefresh={() => {
                      console.log('Refresh button clicked');
                      fetchCreditNotes(1, filters);
                    }}
                    onClose={handleModalClose}
                    onApplyCredit={(creditNote) => {
                      console.log('Apply credit button clicked for:', creditNote);
                      setActiveModal('apply-credit');
                      setApplyForm(prev => ({
                        ...prev,
                        customer_id: creditNote.customer_id,
                        credit_note_id: creditNote.id
                      }));
                    }}
                    onPageChange={(page) => {
                      console.log('Page change requested:', page);
                      setPagination(prev => ({ ...prev, currentPage: page }));
                      fetchCreditNotes(page, filters);
                    }}
                  />
                )}

                {activeModal === 'apply-credit' && (
                  <form onSubmit={handleApplyCredit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
                        <select 
                          value={applyForm.customer_id}
                          onChange={(e) => handleApplyFormChange('customer_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          required
                        >
                          <option value="">Select Customer</option>
                          {customersWithCredits.map(customer => (
                            <option key={customer.customer_id} value={customer.customer_id}>
                              {customer.customer_name} (₵{parseFloat(customer.total_available).toFixed(2)} available)
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Credit Note *</label>
                        <select 
                          value={applyForm.credit_note_id}
                          onChange={(e) => handleApplyFormChange('credit_note_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          required
                        >
                          <option value="">Select Credit Note</option>
                          {applyForm.customer_id && customersWithCredits
                            .find(c => c.customer_id === parseInt(applyForm.customer_id))
                            ?.credit_notes?.map(creditNote => (
                              <option key={creditNote.id} value={creditNote.id}>
                                {creditNote.credit_note_id} - ₵{parseFloat(creditNote.remaining_amount).toFixed(2)} remaining
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Apply Amount *</label>
                        <input 
                          type="number" 
                          step="0.01"
                          min="0"
                          value={applyForm.amount}
                          onChange={(e) => handleApplyFormChange('amount', e.target.value)}
                          placeholder="0.00" 
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" 
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Related Document Type</label>
                        <select 
                          value={applyForm.related_document_type}
                          onChange={(e) => handleApplyFormChange('related_document_type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="">Select Document Type</option>
                          <option value="Invoice">Invoice</option>
                          <option value="Sale">Sale</option>
                          <option value="Proforma">Proforma</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button 
                        type="button"
                        onClick={handleModalClose} 
                        className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        disabled={loading}
                      >
                        {loading ? <Loader2 className="animate-spin" size={16} /> : null}
                        <span>Apply Credit</span>
                      </button>
                    </div>
                  </form>
                )}

                {activeModal === 'credit-reports' && (
                  <CreditReportsModal 
                    reportsData={reportsData}
                    loading={reportsLoading}
                    reportFilters={reportFilters}
                    onFilterChange={handleReportFilterChange}
                    onRefresh={() => {
                      console.log('Reports refresh button clicked');
                      fetchReportsData();
                    }}
                    onClose={handleModalClose}
                    onExportPDF={() => {
                      // TODO: Implement PDF export
                      toast.success('PDF export functionality coming soon!');
                    }}
                    onExportExcel={() => {
                      // TODO: Implement Excel export
                      toast.success('Excel export functionality coming soon!');
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CreditNote;
